import db from './db';
import type { KnittingState, Pattern } from '../types/state';
import { CURRENT_PATTERN_VERSION, CURRENT_STATE_VERSION } from '../types/state';
import type { Session } from '../types/session';
import type { IRPattern } from '../types/ir';
import type { z } from 'zod';
import { PatternSchema, SessionWireSchema } from '../types/schemas';
import { encodeSession, decodeSession } from './eventCodec';
import type { SessionWire } from './eventCodec';
import { archiveHistory } from '../logic/archive';
import { normalizePattern, normalizeSession, patternTitlesFromRaw } from './migrations';
import { bumpLastSavedAt } from './lastSaved';
import { track } from '../analytics/posthog';
import { captureException } from '../analytics';

type WirePattern = z.infer<typeof PatternSchema>;

const STATE_KEY = 'knitting-state';

type ChangeCallback = () => void;
const changeCallbacks: ChangeCallback[] = [];

export function onStateChanged(cb: ChangeCallback): () => void {
  changeCallbacks.push(cb);
  return () => {
    const idx = changeCallbacks.indexOf(cb);
    if (idx >= 0) changeCallbacks.splice(idx, 1);
  };
}

function notifyChange(): void {
  changeCallbacks.forEach((cb) => cb());
}

function emptyState(): KnittingState {
  return { version: CURRENT_STATE_VERSION, patterns: {}, sessions: {} };
}

/**
 * Convert an in-memory Pattern into its on-disk wire form: serialize
 * `structure` to `structureJson` (preserves node IDs across reloads), drop
 * `structure`, and stamp the per-item version.
 */
export function toWirePattern(p: Pattern): WirePattern {
  const wire: WirePattern = {
    version: CURRENT_PATTERN_VERSION,
    id: p.id,
    title: p.title,
    parentPatternId: p.parentPatternId,
    dsl: p.dsl,
  };
  if (p.structure) wire.structureJson = JSON.stringify(p.structure);
  else if (p.structureJson) wire.structureJson = p.structureJson;
  if (p.sourceUrl) wire.sourceUrl = p.sourceUrl;
  if (p.upstreamHash) wire.upstreamHash = p.upstreamHash;
  if (p.builtin !== undefined) wire.builtin = p.builtin;
  if (p.js_source !== undefined) wire.js_source = p.js_source;
  if (p.color !== undefined) wire.color = p.color;
  if (p.pdfRef !== undefined) wire.pdfRef = p.pdfRef;
  return wire;
}

interface ParsedStateBlob {
  version: number;
  patterns: Record<string, Pattern>;
  sessions: Record<string, Session>;
  unknownPatterns: Record<string, unknown>;
  unknownSessions: Record<string, unknown>;
}

/**
 * Parse a persisted state envelope per-item. Each pattern and session is
 * validated independently; failures are preserved as opaque blobs in
 * `unknownPatterns` / `unknownSessions` and round-tripped through saves so a
 * future client that understands the shape can pick them up.
 *
 * The envelope itself is intentionally permissive — any shape that isn't a
 * `{ patterns, sessions }` record is treated as empty. There's no way for
 * envelope-level validation to take down item-level data.
 */
export async function parseStateBlob(raw: unknown): Promise<ParsedStateBlob> {
  const obj = isObject(raw) ? raw : {};
  const rawPatterns = isObject(obj.patterns) ? obj.patterns : {};
  const rawSessions = isObject(obj.sessions) ? obj.sessions : {};
  const version = typeof obj.version === 'number' ? obj.version : CURRENT_STATE_VERSION;

  const patterns: Record<string, Pattern> = {};
  const unknownPatterns: Record<string, unknown> = {};
  for (const [id, p] of Object.entries(rawPatterns)) {
    const result = PatternSchema.safeParse(normalizePattern(p));
    if (!result.success) {
      console.warn(
        `Pattern ${id} failed schema validation; preserving raw blob.`,
        result.error.issues,
      );
      unknownPatterns[id] = p;
      track('unknown_item_preserved', { kind: 'pattern', count: 1 });
      continue;
    }
    let structure = tryParseStructureJson(result.data.structureJson);
    if (!structure) structure = await tryParseDSL(result.data.dsl, result.data.title);
    if (!structure) {
      // Schema-valid but neither the cached IR nor the DSL parses on this
      // client — typically an old client reading newer DSL grammar, or
      // structureJson written against a future IR shape. Quarantine instead
      // of letting the pattern through with no structure.
      console.warn(`Pattern ${id} has unparseable DSL/IR on this client; preserving raw blob.`);
      unknownPatterns[id] = p;
      track('unknown_item_preserved', { kind: 'pattern', count: 1 });
      continue;
    }
    patterns[id] = toDomainPattern(result.data, structure);
  }

  const titles = patternTitlesFromRaw(rawPatterns);
  const sessions: Record<string, Session> = {};
  const unknownSessions: Record<string, unknown> = {};
  for (const [id, s] of Object.entries(rawSessions)) {
    const result = SessionWireSchema.safeParse(normalizeSession(s, titles));
    if (!result.success) {
      console.warn(`Session ${id} failed validation; preserving raw blob.`, result.error.issues);
      unknownSessions[id] = s;
      track('unknown_item_preserved', { kind: 'session', count: 1 });
      continue;
    }
    sessions[id] = decodeSession(result.data);
  }

  return { version, patterns, sessions, unknownPatterns, unknownSessions };
}

function isObject(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === 'object' && !Array.isArray(x);
}

// Strip the wire-only `version` so domain code can't see it.
function toDomainPattern(wire: WirePattern, structure: IRPattern | undefined): Pattern {
  const p: Pattern = {
    id: wire.id,
    title: wire.title,
    parentPatternId: wire.parentPatternId,
    dsl: wire.dsl,
  };
  if (wire.structureJson !== undefined) p.structureJson = wire.structureJson;
  if (structure) p.structure = structure;
  if (wire.sourceUrl) p.sourceUrl = wire.sourceUrl;
  if (wire.upstreamHash) p.upstreamHash = wire.upstreamHash;
  if (wire.builtin !== undefined) p.builtin = wire.builtin;
  if (wire.js_source !== undefined) p.js_source = wire.js_source;
  // Only accept a 6-char hex color into the domain. Anything else (empty
  // string, a future `#rrggbbaa`, named CSS color, etc.) is silently dropped;
  // resolvePatternColor falls back to deriveColor(id) at render time.
  if (typeof wire.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(wire.color)) {
    p.color = wire.color;
  }
  if (wire.pdfRef) p.pdfRef = wire.pdfRef;
  return p;
}

/**
 * Build the on-disk envelope from an in-memory state, merging any
 * `unknownPatterns` / `unknownSessions` back into the records so they survive
 * the save. Stamps `CURRENT_STATE_VERSION` on the envelope; per-item versions
 * are stamped by `toWirePattern` and `encodeSession`.
 */
export function toWireState(state: KnittingState): {
  version: number;
  patterns: Record<string, unknown>;
  sessions: Record<string, unknown>;
} {
  const patterns: Record<string, unknown> = {};
  for (const [id, p] of Object.entries(state.patterns)) patterns[id] = toWirePattern(p);
  for (const [id, blob] of Object.entries(state.unknownPatterns ?? {})) patterns[id] = blob;

  const sessions: Record<string, SessionWire | unknown> = {};
  for (const [id, s] of Object.entries(state.sessions)) sessions[id] = encodeSession(s);
  for (const [id, blob] of Object.entries(state.unknownSessions ?? {})) sessions[id] = blob;

  return { version: CURRENT_STATE_VERSION, patterns, sessions };
}

// Serializes every mutating operation (savePattern, updateSession, etc.) so a
// concurrent fire-and-forget save (e.g. the tracker's auto-save useEffect)
// can't read a stale state snapshot, then write it back over an interleaved
// fork. Without this, two read-modify-write pipelines can race and the older
// snapshot wins, silently dropping the more recent change.
let writeQueue: Promise<unknown> = Promise.resolve();
function serializeWrite<T>(fn: () => Promise<T>): Promise<T> {
  const next = writeQueue.then(fn, fn);
  // Swallow rejections in the queue chain so one failure doesn't poison
  // subsequent writes; the original promise still rejects to the caller.
  writeQueue = next.then(
    () => undefined,
    () => undefined,
  );
  return next;
}

/**
 * Retrieves the full application state from local storage.
 *
 * Sessions are stored on disk in the compact wire form; this decodes them
 * back into the verbose in-memory `KnittingEvent[]` representation that the
 * engine and state machine consume. Per-item validation failures are
 * captured into `unknownPatterns` / `unknownSessions` so a single bad item
 * can't take down the rest of the library.
 */
export async function getFullState(): Promise<KnittingState> {
  try {
    const raw = await db.getItem<unknown>(STATE_KEY);
    if (!raw) return emptyState();
    const { version, patterns, sessions, unknownPatterns, unknownSessions } =
      await parseStateBlob(raw);
    return { version, patterns, sessions, unknownPatterns, unknownSessions };
  } catch (e) {
    captureException(e, { source: 'persistence', operation: 'loadState' });
    throw e;
  }
}

// Restore a pattern structure from a serialized JSON string. Returns undefined
// on any failure so the caller can fall back to DSL re-parsing.
function tryParseStructureJson(json: string | undefined) {
  if (!json) return undefined;
  try {
    return JSON.parse(json);
  } catch {
    return undefined;
  }
}

// Parse a stored pattern's DSL, returning undefined (and warning) on failure
// rather than throwing. This keeps a single corrupt pattern from taking down
// the whole library load.
//
// The parser (Jison-generated grammar, ~8 KB gz) is dynamic-imported on first
// call so it stays out of the eager bundle. For patterns that store
// `structureJson` alongside the DSL (the common case), the fallback never
// fires and the parser chunk is never loaded.
let parseDSLPromise: Promise<typeof import('../logic/parser')['parseDSL']> | null = null;
function getParseDSL() {
  if (!parseDSLPromise) {
    parseDSLPromise = import('../logic/parser').then((m) => m.parseDSL);
  }
  return parseDSLPromise;
}

async function tryParseDSL(dsl: string, title: string): Promise<IRPattern | undefined> {
  try {
    const parseDSL = await getParseDSL();
    return parseDSL(dsl, { context: 'load' });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.warn(`Failed to parse pattern "${title}": ${message}`);
    return undefined;
  }
}

/**
 * Saves the full application state to local storage. Per-item wire conversion
 * (`toWirePattern`, `encodeSession`) stamps version fields; any
 * `unknownPatterns` / `unknownSessions` blobs are merged back in unchanged so
 * a client that couldn't parse them still preserves them on disk.
 */
export async function saveFullState(state: KnittingState): Promise<void> {
  try {
    await db.setItem(STATE_KEY, toWireState(state));
  } catch (e) {
    if (e instanceof Error && (e.name === 'QuotaExceededError' || e.message.toLowerCase().includes('quota'))) {
      track('storage_quota_warning', {});
    } else {
      captureException(e, { source: 'saveFullState' });
    }
    throw e;
  }
  await bumpLastSavedAt();
  notifyChange();
}

/**
 * Returns all patterns in the user's library.
 */
export async function getLibrary(): Promise<Record<string, Pattern>> {
  const state = await getFullState();
  return state.patterns;
}

/**
 * Saves or updates a pattern in the library.
 */
export async function savePattern(pattern: Pattern): Promise<void> {
  return serializeWrite(async () => {
    const state = await getFullState();
    state.patterns[pattern.id] = pattern;
    await saveFullState(state);
  });
}

/**
 * Removes a pattern from the library. With `cascadeSessions: true`, also
 * removes every session that references it. Returns the number of sessions
 * deleted (always 0 unless cascading).
 */
export async function deletePattern(
  patternId: string,
  opts: { cascadeSessions?: boolean } = {},
): Promise<{ deletedSessions: number }> {
  return serializeWrite(async () => {
    const state = await getFullState();
    // Capture the orphaned PDF blob id BEFORE the pattern record is dropped —
    // once it's gone the only pointer to the local-only blob is lost. Cleanup
    // happens after saveFullState; failure to delete the blob is best-effort
    // (the worst case is wasted IDB quota, not data corruption).
    const orphanedPdfBlobId = state.patterns[patternId]?.pdfRef?.id;

    delete state.patterns[patternId];

    let deletedSessions = 0;
    if (opts.cascadeSessions) {
      for (const [id, s] of Object.entries(state.sessions)) {
        if (s.patternId === patternId) {
          delete state.sessions[id];
          deletedSessions++;
        }
      }
    }

    await saveFullState(state);
    if (orphanedPdfBlobId) {
      const { deletePdfBlob } = await import('./pdf');
      await deletePdfBlob(orphanedPdfBlobId).catch(() => undefined);
    }
    return { deletedSessions };
  });
}

/**
 * Saves or updates a session. Bumps `updatedAt`; `startedAt` is set on first
 * save if missing (older Session records may not have it).
 */
export async function updateSession(session: Session): Promise<void> {
  return serializeWrite(async () => {
    const state = await getFullState();
    const now = new Date().toISOString();
    state.sessions[session.id] = {
      ...session,
      startedAt: session.startedAt || now,
      updatedAt: now,
    };
    await saveFullState(state);
  });
}

/**
 * Repoints a session at a new pattern (used after lazy fork) WITHOUT
 * disturbing any other session fields. Reads the latest session from disk
 * before writing, so it never clobbers history/snapshot/timer that an
 * in-flight auto-save just persisted.
 */
export async function repointSession(sessionId: string, newPatternId: string): Promise<void> {
  return serializeWrite(async () => {
    const state = await getFullState();
    const session = state.sessions[sessionId];
    if (!session) return;
    state.sessions[sessionId] = {
      ...session,
      patternId: newPatternId,
      updatedAt: new Date().toISOString(),
    };
    await saveFullState(state);
  });
}

/**
 * Removes a session entirely.
 */
export async function deleteSession(sessionId: string): Promise<void> {
  return serializeWrite(async () => {
    const state = await getFullState();
    delete state.sessions[sessionId];
    await saveFullState(state);
  });
}

/**
 * Compacts a session: collapses `history` into per-row timing tuples and marks
 * the session as archived. Irreversible — raw events are gone after this.
 */
export async function archiveSession(sessionId: string): Promise<void> {
  return serializeWrite(async () => {
    const state = await getFullState();
    const session = state.sessions[sessionId];
    if (!session) return;

    const { rowTimings, totalTimeMs, currentRow } = archiveHistory(session.history);
    state.sessions[sessionId] = {
      ...session,
      status: 'archived',
      history: [],
      rowTimings,
      totalTimeMs,
      snapshot: {
        ...session.snapshot,
        currentRow,
        totalTimeMs,
      },
    };
    await saveFullState(state);
  });
}

/**
 * Marks a session as completed (soft, reversible). History is preserved.
 */
export async function completeSession(sessionId: string): Promise<void> {
  return serializeWrite(async () => {
    const state = await getFullState();
    const session = state.sessions[sessionId];
    if (!session) return;
    state.sessions[sessionId] = { ...session, status: 'completed' };
    await saveFullState(state);
  });
}

/**
 * Reverts a completed session back to paused. Used by the Complete screen's
 * "Undo / keep knitting" action.
 */
export async function uncompleteSession(sessionId: string): Promise<void> {
  return serializeWrite(async () => {
    const state = await getFullState();
    const session = state.sessions[sessionId];
    if (!session) return;
    state.sessions[sessionId] = { ...session, status: 'paused' };
    await saveFullState(state);
  });
}

/**
 * Serializes the application state to a JSON string.
 */
export function exportState(state: KnittingState): string {
  return JSON.stringify(toWireState(state));
}

/**
 * Parses a JSON string into an in-memory KnittingState. Per-item validation
 * failures become opaque blobs in `unknownPatterns` / `unknownSessions` and
 * round-trip through saves; the import never throws on a single bad item.
 */
export async function importState(json: string): Promise<KnittingState> {
  const { version, patterns, sessions, unknownPatterns, unknownSessions } = await parseStateBlob(
    JSON.parse(json),
  );
  return { version, patterns, sessions, unknownPatterns, unknownSessions };
}
